<template>
  <div :class="$style.wrapper">
    <header class="flex items-center gap-2" :class="$style.header">
      <h2>
        {{ $t('notifications:title') }}
      </h2>
      <CButton
        v-if="notificationsStore.unreadCount"
        size="sml"
        color="white"
        width="fluid-width"
        style="margin-left: auto"
        icon="mark_email_read"
        @click="notificationsStore.markAllRead()"
      >
        {{ $t('notifications:markAllRead') }}
        {{ `(${notificationsStore.unreadCount})` }}
      </CButton>
    </header>
    <ul v-if="notificationsList.length">
      <li
        v-for="notification in notificationsList"
        :key="notification.id"
        :class="{ [$style.unread]: !notification.readAt }"
        @click="handleNotificationClick(notification)"
      >
        <header :class="$style.notificationHeader">
          <div>
            <strong
              >{{ notification.order.shortId }} -
              {{ notification.title }}</strong
            >
          </div>
          <time
            :class="$style.notificationTime"
            :title="$format.date(notification.createdAt, 'full', 'long')"
            >{{ $format.relativeTime(notification.createdAt) }}</time
          >
        </header>
        <p>{{ notification.message }}</p>
      </li>
    </ul>
    <div v-else>
      <p class="px-4">
        {{ $t('notifications:empty') }}
      </p>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useFormat, useRouter } from '@/composables/plugins';
import type {
  Notification,
  NotificationType,
  NewMessageNotificationData,
  NewFileCommentNotificationData,
  DownloadJobCompletedNotificationData,
  DeliveryScheduleUpdateNotificationData,
  ConversationResolvedNotificationData,
  RfqPartnerQuoteUploadedNotificationData,
  RfqNotificationData,
  FileAddedNotificationData,
  FileRevisedNotificationData,
  MilestoneOverdueNotificationData,
  PricingChangeNotificationData,
  CallMissedNotificationData,
  CallCustomerPartnerNotificationData,
  PricingDocumentTranslationNotificationData,
} from '@cutr/constants/notifications';
import { useNotificationStore } from '@/stores/notifications';
import { computed } from 'vue';
import { useTranslation } from '@/composables/plugins';
import { useActiveUserStore } from '@/stores/user';

const emit = defineEmits(['close']);

defineProps<{ withCounter?: boolean }>();

const notificationsStore = useNotificationStore();
const router = useRouter();
const { t } = useTranslation();
const format = useFormat();

const userStore = useActiveUserStore();

const handleNotificationClick = (
  notification: (typeof notificationsList.value)[number]
) => {
  markRead(notification);
  notification.onClick();
  emit('close');
};

const pushToOrder = ({
  orderId,
  name,
  query,
  params,
}: {
  orderId: string;
  name: string;
  query?: Record<string, string | undefined>;
  params?: Record<string, string | undefined>;
}) => {
  const current = router.currentRoute.value;
  const isSameOrder = current.params.id === orderId;

  router.push({
    name,
    params: { id: orderId, ...params },
    query: {
      // preserve order-level params when navigating within the same order
      ...(isSameOrder && current.query),
      // notification-specific params override preserved ones
      ...query,
    },
  });
};

// Orders using Positions have moved files under a position, so route straight
// there when we know the position, or to the inbox when the file hasn't been
// assigned to one yet. Orders not on Positions keep the legacy files tab.
const pushToFile = ({
  orderId,
  variantId,
  positionId,
  isPositionsOrder,
  fileId,
}: {
  orderId: string;
  variantId?: string;
  positionId?: string | null;
  isPositionsOrder?: boolean;
  fileId?: string;
}) => {
  const query = { variantId, fileId };

  if (positionId) {
    return pushToOrder({
      orderId,
      name: 'order:positions:position',
      params: { positionId },
      query,
    });
  }

  if (isPositionsOrder) {
    return pushToOrder({ orderId, name: 'order:positions:inbox', query });
  }

  return pushToOrder({ orderId, name: 'order:files', query });
};

const pushToNotificationFile = (
  n: Notification<{ orderVariantId?: string; positionId?: string | null }>,
  fileId?: string
) =>
  pushToFile({
    orderId: n.order.id,
    variantId: n.data?.orderVariantId,
    positionId: n.data?.positionId,
    isPositionsOrder: n.order.isPositions,
    fileId,
  });

const NOTIFICATION_GENERATORS: Record<
  NotificationType,
  (n: Notification) => {
    title: string;
    message: string;
    onClick: () => void;
  }
> = {
  new_conversation_message: (notification: Notification) => {
    const n = notification as Notification<NewMessageNotificationData>;
    return {
      title: t(`notifications:newConversationMessage.title`),
      message: `${n.data?.author?.name || 'Unknown'}: ${truncate(
        n.data?.message,
        120
      )}`,
      onClick: () =>
        pushToOrder({
          orderId: n.order.id,
          name: 'order:messages',
          query: { conversationId: n.resourceId },
        }),
    };
  },

  pricing_document_translation_completed: (notification: Notification) =>
    pricingDocumentTranslation(
      notification,
      'pricingDocumentTranslationCompleted'
    ),
  pricing_document_translation_failed: (notification: Notification) =>
    pricingDocumentTranslation(
      notification,
      'pricingDocumentTranslationFailed'
    ),

  new_file_comment: (notification: Notification) => {
    const n = notification as Notification<NewFileCommentNotificationData>;
    return {
      title: t(`notifications:newFileComment.title`),
      message: `${n.data?.authorName || 'Unknown'}: ${truncate(
        n.data?.commentBody,
        120
      )}`,
      onClick: () => pushToNotificationFile(n, n.data?.cadFileId),
    };
  },

  rfq_partner_quote_uploaded: (notification: Notification) => {
    const n =
      notification as Notification<RfqPartnerQuoteUploadedNotificationData>;
    return {
      title: t(`notifications:rfqPartnerQuoteUploaded.title`),
      message: t(`notifications:rfqPartnerQuoteUploaded.message`, {
        partnerOrganisationName: n.data?.partnerOrganisationName,
      }),
      onClick: () =>
        router.push({
          name: 'rfq',
          params: { id: n.data?.rfqId },
        }),
    };
  },

  rfq_completed: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqCompleted.title`),
      message: t(`notifications:rfqCompleted.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  rfq_dispatched_to_partner: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqDispatchedToPartner.title`),
      message: t(`notifications:rfqDispatchedToPartner.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  rfq_description_updated: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqDescriptionUpdated.title`),
      message: t(`notifications:rfqDescriptionUpdated.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  rfq_response_due_on_updated: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqResponseDueOnUpdated.title`),
      message: t(`notifications:rfqResponseDueOnUpdated.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  rfq_pricing_template_updated: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqPricingTemplateUpdated.title`),
      message: t(`notifications:rfqPricingTemplateUpdated.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  rfq_without_prices_is_due_soon: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqWithoutPricesIsDueSoon.title`),
      message: t(`notifications:rfqWithoutPricesIsDueSoon.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  rfq_awarded: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqAwarded.title`),
      message: t(`notifications:rfqAwarded.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  rfq_lost: (notification: Notification) => {
    const n = notification as Notification<RfqNotificationData>;
    return {
      title: t(`notifications:rfqLost.title`),
      message: t(`notifications:rfqLost.message`),
      onClick: () =>
        router.push({ name: 'rfq', params: { id: n.data?.rfqId } }),
    };
  },

  offer_awarded: (n) => ({
    title: t(`notifications:offerAwarded.title`),
    message: t(`notifications:offerAwarded.message`),
    onClick: () => pushToOrder({ orderId: n.order.id, name: 'order' }),
  }),

  offer_available: (n) => ({
    title: t(`notifications:offerAvailable.title`),
    message: t(`notifications:offerAvailable.message`),
    onClick: () =>
      pushToOrder({
        orderId: n.order.id,
        name: 'order:quotes',
        query: { proposalId: n.resourceId },
      }),
  }),

  offer_missed: (n) => ({
    title: t(`notifications:offerMissed.title`),
    message: t(`notifications:offerMissed.message`),
    onClick: () => {},
  }),

  nesting_jobs_completed: (n) => ({
    title: t('notifications:nestingJobsCompleted.title'),
    message: t('notifications:nestingJobsCompleted.message', {
      shortOrderId: n.order.shortId,
    }),
    onClick: () =>
      pushToOrder({
        orderId: n.order.id,
        name: 'order:material-assignment',
        query: { variantId: n.resourceId, openNesting: 'true' },
      }),
  }),

  download_job_completed: (notification: Notification) => {
    const n =
      notification as Notification<DownloadJobCompletedNotificationData>;
    return {
      title: t(`notifications:downloadJobCompleted.title`),
      message: t(`notifications:downloadJobCompleted.message`),
      onClick: () => {
        if (!n.data?.downloadURL) return;
        window.open(n.data.downloadURL, '_blank');
      },
    };
  },

  download_job_failed: (n) => ({
    title: t('notifications:downloadJobFailed.title'),
    message: t('notifications:downloadJobFailed.message'),
    onClick: () => {},
  }),

  customer_invoice_open: (n) => ({
    title: t('notifications:customerInvoiceOpen.title'),
    message: t('notifications:customerInvoiceOpen.message'),
    onClick: () => pushToOrder({ orderId: n.order.id, name: 'order:billing' }),
  }),

  delivery_schedule_update_requested: (notification: Notification) => {
    const n =
      notification as Notification<DeliveryScheduleUpdateNotificationData>;
    return {
      title: t('notifications:deliveryScheduleUpdate.title'),
      message: n.data?.reason || '',
      onClick: () =>
        pushToOrder({ orderId: n.order.id, name: 'order:delivery' }),
    };
  },

  file_revised: (notification: Notification) => {
    const n = notification as Notification<FileRevisedNotificationData>;
    let author = n.data.authorName;
    if (n.data.authorType) {
      author = `${author} (${n.data.authorType})`;
    }
    return {
      title: t(`notifications:fileRevised.title`),
      message: userStore.isAdmin
        ? t(`notifications:fileRevised.messageAdmin`, { author })
        : t(`notifications:fileRevised.message`),
      onClick: () => pushToNotificationFile(n, n.resourceId),
    };
  },

  file_added: (notification: Notification) => {
    const n = notification as Notification<FileAddedNotificationData>;
    let author = n.data.authorName;
    if (n.data.authorType) {
      author = `${author} (${n.data.authorType})`;
    }

    return {
      title: t(`notifications:fileAdded.title`),
      message: userStore.isAdmin
        ? t(`notifications:fileAdded.messageAdmin`, { author })
        : t(`notifications:fileAdded.message`),
      onClick: () => pushToNotificationFile(n, n.resourceId),
    };
  },

  milestone_overdue: (notification: Notification) => {
    const n = notification as Notification<MilestoneOverdueNotificationData>;
    return {
      title: t(`notifications:milestoneOverdue.title`, {
        count: n.data?.milestones.length,
      }),
      message: t(`notifications:milestoneOverdue.message`, {
        count: n.data?.milestones.length,
        milestones: n.data?.milestones.map((m) => m.name),
      }),
      onClick: () =>
        pushToOrder({ orderId: n.order.id, name: 'order:timeline' }),
    };
  },

  conversation_resolved: (notification: Notification) => {
    const n =
      notification as Notification<ConversationResolvedNotificationData>;
    return {
      title: t(`notifications:conversationResolved.title`),
      message: t(`notifications:conversationResolved.message`),
      onClick: () => pushToNotificationFile(n, n.resourceId),
    };
  },

  pricing_change_submitted: (notification: Notification) => {
    const n = notification as Notification<PricingChangeNotificationData>;
    const tData = getPricingChangeNotificationData(n);
    return {
      title: t(`notifications:pricingChangeSubmitted.title`, tData),
      message: t(`notifications:pricingChangeSubmitted.message`, tData),
      onClick: () =>
        pushToOrder({
          orderId: n.order.id,
          name: 'order:quotes',
          query: { pricingChangeId: n.resourceId },
        }),
    };
  },

  pricing_change_pending: (notification: Notification) => {
    const n = notification as Notification<PricingChangeNotificationData>;
    const tData = getPricingChangeNotificationData(n);
    return {
      title: t(`notifications:pricingChangePending.title`, tData),
      message: t(`notifications:pricingChangePending.message`, tData),
      onClick: () =>
        pushToOrder({
          orderId: n.order.id,
          name: 'order:quotes',
          query: { pricingChangeId: n.resourceId },
        }),
    };
  },

  pricing_change_accepted: (notification: Notification) => {
    const n = notification as Notification<PricingChangeNotificationData>;
    const tData = getPricingChangeNotificationData(n);
    return {
      title: t(`notifications:pricingChangeAccepted.title`, tData),
      message: t(`notifications:pricingChangeAccepted.message`, tData),
      onClick: () =>
        pushToOrder({
          orderId: n.order.id,
          name: 'order:quotes',
          query: { pricingChangeId: n.resourceId },
        }),
    };
  },

  pricing_change_declined: (notification: Notification) => {
    const n = notification as Notification<PricingChangeNotificationData>;
    const tData = getPricingChangeNotificationData(n);
    return {
      title: t(`notifications:pricingChangeDeclined.title`, tData),
      message: t(`notifications:pricingChangeDeclined.message`, tData),
      onClick: () =>
        pushToOrder({
          orderId: n.order.id,
          name: 'order:quotes',
          query: { pricingChangeId: n.resourceId },
        }),
    };
  },

  pricing_change_partially_accepted: (notification: Notification) => {
    const n = notification as Notification<PricingChangeNotificationData>;
    const tData = getPricingChangeNotificationData(n);
    return {
      title: t(`notifications:pricingChangePartiallyAccepted.title`, tData),
      message: t(`notifications:pricingChangePartiallyAccepted.message`, tData),
      onClick: () =>
        pushToOrder({
          orderId: n.order.id,
          name: 'order:quotes',
          query: { pricingChangeId: n.resourceId },
        }),
    };
  },

  call_missed: (notification: Notification) => {
    const n = notification as Notification<CallMissedNotificationData>;
    const { conversationId, organisationId, requestForQuoteId } = n.data || {};
    return {
      title: t(`notifications:callMissed.title`),
      message: t(
        `notifications:callMissed.${
          requestForQuoteId ? 'messageRFQ' : 'message'
        }`,
        n.data
      ),
      onClick: () => {
        if (requestForQuoteId) {
          return router.push({
            name: 'rfq',
            params: { id: requestForQuoteId },
            query: { organisationId },
          });
        }

        return pushToOrder({
          orderId: n.order.id,
          name: 'order:messages',
          query: { conversationId: conversationId || undefined },
        });
      },
    };
  },

  call_customer_partner: (notification: Notification) => {
    const n = notification as Notification<CallCustomerPartnerNotificationData>;
    return {
      title: t(`notifications:callCustomerPartner.title`, {
        fromUserType: t(`common:${n.data?.fromUserType || 'Unknown'}`),
        toUserType: t(`common:${n.data?.toUserType || 'Unknown'}`),
      }),
      message: t('notifications:callCustomerPartner.message', {
        ...n.data,
        fromUser: n.data?.fromUserLabel || 'Unknown',
        toUser: n.data?.toUserLabel || 'Unknown',
      }),
      onClick: () =>
        router.push({
          name: 'calls:details',
          params: { id: n.resourceId },
        }),
    };
  },
};

const notificationsList = computed(() =>
  notificationsStore.notifications.map((n) => ({
    ...n,
    ...NOTIFICATION_GENERATORS[n.type](n),
  }))
);

function markRead({ id, readAt }: Notification) {
  if (readAt) return;
  notificationsStore.markRead({ id: [id] });
}

function truncate(text: string, length: number) {
  if (text?.length > length) {
    return text.substring(0, length) + '...';
  }
  return text;
}

function pricingDocumentTranslation(
  notification: Notification,
  translationKey: string
) {
  const n =
    notification as Notification<PricingDocumentTranslationNotificationData>;
  const query = n.data.originalProposalId
    ? {
        docId: n.data.originalProposalId,
        sideDocId: n.data.proposalId,
      }
    : { docId: n.data.proposalId };
  return {
    title: t(`notifications:${translationKey}.title`),
    message: t(`notifications:${translationKey}.message`),
    onClick: () =>
      pushToOrder({ orderId: n.order.id, name: 'order:pricing', query }),
  };
}

function getPricingChangeNotificationData(
  notification: Notification<PricingChangeNotificationData>
) {
  const n = notification as Notification<PricingChangeNotificationData>;
  return {
    ...n.data,
    dueDate: n.data?.deadlineAt ? format.date(n.data?.deadlineAt, 'long') : '',
    total: format.currency(n.data?.totalAmount, n.data?.currency),
  };
}
</script>
<style module>
.wrapper {
  display: grid;

  & ul {
    font-size: 0.875rem;
  }

  & li {
    padding: 1rem 1rem 0.5rem;
    position: relative;
    border-bottom: 1px solid var(--neutral-300);
  }

  & p {
    margin: 0.5rem 0;
  }

  & li:hover {
    background-color: var(--neutral-100);
    cursor: pointer;
  }
}

.counter {
  border-radius: 100%;
  width: 1.2rem;
  height: 1.2rem;
  background-color: var(--red-500);
  color: var(--neutral-50);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border: 1px solid var(--neutral-50);
}

.unread {
  background-color: var(--amber-100);
}

.unread .notificationTime::after {
  content: '';
  display: block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 100%;
  background-color: var(--amber-600);
  margin-left: 0.5rem;
}

.header {
  top: 0;
  z-index: 1;
  position: sticky;
  background-color: var(--neutral-50);
  border-bottom: 1px solid var(--neutral-300);
  justify-content: space-between;
  padding: 1rem;
}

.notificationHeader {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.notificationTime {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: var(--neutral-600);
}
</style>
